/**
 * Manejo centralizado del cierre de sesión por token inválido.
 *
 * El backend (core / CheckJWT) invalida el JWT anterior cuando la misma cuenta
 * inicia sesión en otro navegador: a partir de ese momento cada petición del
 * navegador viejo responde 401/403 con `data.clean = true`. Este módulo traduce
 * esa respuesta en un cierre de sesión controlado: limpia la sesión local,
 * muestra un único aviso de "sesión finalizada" en la pantalla de login y
 * redirige, sin reintentar ni acumular errores técnicos en pantalla.
 */
import { destroyCookies } from '../components/login/login_logic';

// Evita que varias peticiones fallidas en paralelo disparen múltiples avisos /
// redirecciones. La primera gana; el resto no hace nada hasta la recarga.
let handling = false;

// Marca un logout intencional del usuario para no mostrarle el aviso de
// "se inició sesión en otro dispositivo" cuando él mismo pulsó "Cerrar sesión".
let loggingOut = false;

export const SESSION_ENDED_NOTICE_KEY = 'sessionEndedNotice';
export const LOGOUT_NOTICE_KEY = 'logoutNotice';

/**
 * Indica si una respuesta corresponde a una sesión inválida.
 * @param {number} [status] Código HTTP de la respuesta.
 * @param {{clean?: boolean}} [body] Cuerpo `data` de la respuesta.
 */
export const isSessionInvalidResponse = (status, body) =>
  status === 401 || status === 403 || Boolean(body && body.clean === true);

/**
 * Debe llamarse al inicio de un logout intencional para suprimir el aviso de
 * "otro dispositivo" en el flujo de cierre por token inválido.
 */
export const beginIntentionalLogout = () => {
  loggingOut = true;
};

/**
 * Cierre de sesión controlado por token inválido. Idempotente: sólo la primera
 * invocación tiene efecto. Limpia la sesión local, deja un aviso flash para la
 * pantalla de login y recarga en la raíz.
 */
export const handleSessionExpired = () => {
  if (handling) return;
  handling = true;

  localStorage.removeItem('tokenExpirationDateStored');
  destroyCookies();

  if (!loggingOut) {
    try {
      sessionStorage.setItem(
        SESSION_ENDED_NOTICE_KEY,
        'Tu sesión finalizó porque se inició sesión con esta cuenta en otro dispositivo.'
      );
    } catch (e) {
      // sessionStorage puede no estar disponible; la redirección sigue adelante.
    }
  }

  // Recarga completa: corta en seco cualquier petición en vuelo y limpia el
  // estado de React, de modo que no reaparezcan errores tras la redirección.
  window.location.assign('/');
};
